import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import PauseIcon from "@material-ui/icons/Pause";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import RefreshIcon from "@material-ui/icons/Refresh";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const useStyles = makeStyles(theme => ({
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    tableContainer: {
        marginTop: 20,
        maxHeight: 400,
        overflowY: "auto"
    }
}));

const CampaignConfigModal = ({ open, onClose, campaignId }) => {
    const classes = useStyles();
    const [loading, setLoading] = useState(false);
    const [remoteCampaigns, setRemoteCampaigns] = useState([]);

    const fetchRemoteCampaigns = async () => {
        if (!campaignId) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/campaigns/${campaignId}/remote`);
            setRemoteCampaigns(data);
        } catch (err) {
            toastError(err);
        }
        setLoading(false);
    };

    useEffect(() => {
        if (open && campaignId) {
            fetchRemoteCampaigns();
            const interval = setInterval(fetchRemoteCampaigns, 5000); // Auto-refresh every 5s
            return () => clearInterval(interval);
        }
    }, [open, campaignId]);

    const handleControl = async (folderId, action) => {
        try {
            await api.post(`/campaigns/${campaignId}/remote/control`, { folderId, action });
            toast.success(i18n.t("campaigns.toasts.controlled"));
            fetchRemoteCampaigns();
        } catch (err) {
            toastError(err);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            scroll="paper"
            PaperProps={{ elevation: 8 }}
            BackdropProps={{ style: { backgroundColor: "rgba(0, 0, 0, 0.6)" } }}
        >
            <DialogTitle id="form-dialog-title">
                {i18n.t("campaigns.configModal.title")}
                <IconButton onClick={fetchRemoteCampaigns} size="small" style={{ marginLeft: 10 }}>
                    <RefreshIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {loading && <CircularProgress size={24} style={{ marginBottom: 10 }} />}
                <Typography variant="body2" color="textSecondary" gutterBottom>
                    {i18n.t("campaigns.configModal.description")}
                </Typography>
                <div className={classes.tableContainer}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Info</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Progresso</TableCell>
                                <TableCell>Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {remoteCampaigns.map((camp) => (
                                <TableRow key={camp.id}>
                                    <TableCell>{camp.id}</TableCell>
                                    <TableCell>{camp.info}</TableCell>
                                    <TableCell>{camp.status}</TableCell>
                                    <TableCell>
                                        {camp.log_sucess}/{camp.log_total}
                                    </TableCell>
                                    <TableCell>
                                        {camp.status !== "done" && (
                                            <>
                                                {camp.status === "paused" ? (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleControl(camp.id, "continue")}
                                                        title="Continuar"
                                                    >
                                                        <PlayArrowIcon />
                                                    </IconButton>
                                                ) : (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleControl(camp.id, "stop")}
                                                        title="Pausar"
                                                    >
                                                        <PauseIcon />
                                                    </IconButton>
                                                )}
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleControl(camp.id, "delete")}
                                                    title="Deletar"
                                                    color="secondary"
                                                >
                                                    <DeleteOutlineIcon />
                                                </IconButton>
                                            </>
                                        )}
                                        {camp.status === "done" && (
                                            <IconButton
                                                size="small"
                                                disabled
                                                title="Concluída"
                                            >
                                                <DeleteOutlineIcon style={{ opacity: 0.3 }} />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {remoteCampaigns.length === 0 && !loading && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        Nenhuma campanha encontrada na API.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    {i18n.t("common.close")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CampaignConfigModal;
